import { faBullseyePointer, faEnvelope, faMapMarkerAlt, faPhone, faPlus, faStickyNote, faTags, faTrash, faTrashUndo } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Flex, Input, Segmented, Select, Space, Switch, Typography } from "antd"
import dayjs from "dayjs"
import { useEffect, useMemo, useRef, useState } from "react"
import { getAll as getAllCountries } from "../../services/countriesService"
import { getForCountry } from "../../services/statesService"
import GooglePlacesAutoCompleteInput from "../common/GooglePlacesAutoCompleteInput"
import { PlaceData } from "../common/googlePlacesAutoCompleteModel"
import VSpace from "../common/VSpace"
import { CustomerDetail, CustomerEmail, CustomerPhone } from "./customerApi"
import { CustomerFormValues, effectiveSinceDate, hasUnsavedCustomerChanges, toCustomerFormValues } from "./customerInfoFormModel"
import { createNewEmail, createNewPhone, deriveSinceAtSwitch } from "./customersModel"
import SinceDateOverrideModal from "./SinceDateOverrideModal"
import { SinceDateField, SinceDateOverride } from "./sinceDateOverrideModel"

const { Text } = Typography

const displayAsOptions = [
  { value: 0, label: 'Default' },
  { value: 1, label: 'First Last name' },
  { value: 2, label: 'Company' },
  { value: 3, label: 'Last, First name' },
  { value: 4, label: 'Company, First Last' },
  { value: 5, label: 'Company, Last, First' },
]

const phoneTypeOptions = [
  { value: 0, label: 'Company' },
  { value: 1, label: 'Home' },
  { value: 2, label: 'Mobile' },
  { value: 3, label: '(Other)' },
]

const countryOptions = getAllCountries().map(c => ({ label: c.name, value: c.code }))

const buildStateOptions = (countryCode: string | null | undefined) =>
  (getForCountry(countryCode ?? '') ?? []).map(s => ({ label: s.name, value: s.code }))

const getStateLabel = (countryCode: string | null | undefined) =>
  getAllCountries().find(c => c.code === countryCode)?.stateLabel ?? 'State / Province / Region'

const formatSince = (value: string | null | undefined) => (value ? dayjs(value).format('MMM D, YYYY') : '-')

type Props = {
  customer: CustomerDetail
  salesLeads: string[]
  onAddSalesLead?: (selectSalesLead: (name: string) => void) => void
  googleMapsApiKey?: string
  disabled?: boolean
  tagOptions?: string[]
  onTagSearch?: (query: string) => void
  onChange?: (values: CustomerFormValues) => void
  onDirtyChange?: (dirty: boolean) => void
  consultsSlot?: React.ReactNode
}

const CustomerInfoForm = ({
  customer,
  salesLeads,
  onAddSalesLead,
  googleMapsApiKey,
  disabled = false,
  tagOptions,
  onTagSearch,
  onChange,
  onDirtyChange,
  consultsSlot,
}: Props) => {
  const [values, setValues] = useState<CustomerFormValues>(() => toCustomerFormValues(customer))
  const [sinceOverride, setSinceOverride] = useState<SinceDateOverride | null>(null)
  const valuesRef = useRef(values)
  const baselineRef = useRef<CustomerFormValues>(toCustomerFormValues(customer))

  useEffect(() => {
    const next = toCustomerFormValues(customer)
    setValues(next)
    valuesRef.current = next
    baselineRef.current = next
    onDirtyChange?.(false)
  }, [customer])

  const stateOptions = useMemo(() => buildStateOptions(values.countryCode), [values.countryCode])
  const stateLabel = getStateLabel(values.countryCode)

  const apply = (next: CustomerFormValues) => {
    valuesRef.current = next
    setValues(next)
    onChange?.(next)
    onDirtyChange?.(hasUnsavedCustomerChanges(next, baselineRef.current))
  }

  const update = (patch: Partial<CustomerFormValues>) => apply({ ...values, ...patch })

  const selectSalesLead = (name: string) => {
    const latest = valuesRef.current
    apply({ ...latest, selectedSalesLeads: { ...latest.selectedSalesLeads, [name]: true } })
  }

  const handlePlaceData = (data: PlaceData) => {
    const options = buildStateOptions(data.countryCode)
    apply({
      ...values,
      address1: data.address1,
      city: data.city,
      zip: data.zip,
      county: data.county,
      countryCode: data.countryCode,
      state: options.length > 0 ? data.stateShort : data.stateLong,
      location: data.location ?? values.location,
    })
  }

  const handleIsCustomerChange = (isCustomer: boolean) => {
    const { customerSinceAtSwitch, leadSinceAtSwitch } = deriveSinceAtSwitch({ ...values, isCustomer }, dayjs().format('MMM D, YYYY'))
    apply({ ...values, isCustomer, customerSinceAtSwitch, leadSinceAtSwitch })
  }

  const updatePhone = (index: number, patch: Partial<CustomerPhone>) => {
    const phones = values.phones.map((p, i) => (i === index ? { ...p, ...patch } : p))
    update({ phones })
  }

  const removePhone = (index: number) => {
    const phone = values.phones[index]
    if (phone.id === 0) {
      update({ phones: values.phones.filter((_, i) => i !== index) })
    } else {
      updatePhone(index, { removed: !phone.removed })
    }
  }

  const updateEmail = (index: number, patch: Partial<CustomerEmail>) => {
    const emails = values.emails.map((e, i) => (i === index ? { ...e, ...patch } : e))
    update({ emails })
  }

  const removeEmail = (index: number) => {
    const email = values.emails[index]
    if (email.id === 0) {
      update({ emails: values.emails.filter((_, i) => i !== index) })
    } else {
      updateEmail(index, { removed: !email.removed })
    }
  }

  const leadSinceDisplay = formatSince(effectiveSinceDate(values.customLeadSince, values.leadSince, values.leadSinceAtSwitch))
  const customerSinceDisplay = formatSince(effectiveSinceDate(values.customCustomerSince, values.customerSince, values.customerSinceAtSwitch))

  return <div style={{ opacity: disabled ? 0.6 : 1, pointerEvents: disabled ? 'none' : 'auto' }}>
    <section>
      <Flex align="center" gap={32} wrap="wrap">
        <Flex align="center" gap={10}>
          <Switch checked={values.isActive} onChange={isActive => update({ isActive })} />
          <Text>Active Account</Text>
        </Flex>

        <Flex align="center" gap={16} wrap="wrap">
          <Segmented
            value={values.isCustomer ? 'client' : 'lead'}
            onChange={value => handleIsCustomerChange(value === 'client')}
            options={[{ label: 'Lead', value: 'lead' }, { label: 'Client', value: 'client' }]}
          />
          <Text
            type="secondary"
            style={{ cursor: 'pointer' }}
            onClick={() => setSinceOverride({
              currentDate: effectiveSinceDate(values.customLeadSince, values.leadSince),
              standardDate: values.leadSince ?? null,
              fieldName: 'customLeadSince',
              type: 'lead',
            })}
          >
            <small>Lead since</small> {leadSinceDisplay}
          </Text>
          <Text
            type="secondary"
            style={{ cursor: 'pointer' }}
            onClick={() => setSinceOverride({
              currentDate: effectiveSinceDate(values.customCustomerSince, values.customerSince),
              standardDate: values.customerSince ?? null,
              fieldName: 'customCustomerSince',
              type: 'customer',
            })}
          >
            <small>Client since</small> {customerSinceDisplay}
          </Text>
        </Flex>
      </Flex>

      <Flex wrap="wrap" gap={16} style={{ marginTop: 24 }}>
        <FormField label="Company" style={{ width: 300 }}>
          <Input maxLength={75} value={values.company ?? ''} onChange={e => update({ company: e.target.value })} />
        </FormField>
        <FormField label="Display As" style={{ width: 200 }}>
          <Select value={values.displayAs} options={displayAsOptions} onChange={displayAs => update({ displayAs })} style={{ width: '100%' }} />
        </FormField>
        <FormField label="First Name" style={{ width: 200 }}>
          <Input maxLength={25} value={values.firstName ?? ''} onChange={e => update({ firstName: e.target.value })} />
        </FormField>
        <FormField label="Last Name" style={{ width: 200 }}>
          <Input maxLength={35} value={values.lastName ?? ''} onChange={e => update({ lastName: e.target.value })} />
        </FormField>
      </Flex>
      <Flex wrap="wrap" gap={16} style={{ marginTop: 12 }}>
        <div style={{ width: 300 + 16 + 200, flexShrink: 0 }} aria-hidden="true" />
        <FormField label={<span>First Name <small>secondary contact</small></span>} style={{ width: 200 }}>
          <Input maxLength={25} value={values.firstName2 ?? ''} onChange={e => update({ firstName2: e.target.value })} />
        </FormField>
        <FormField label={<span>Last Name <small>secondary contact</small></span>} style={{ width: 200 }}>
          <Input maxLength={35} value={values.lastName2 ?? ''} onChange={e => update({ lastName2: e.target.value })} />
        </FormField>
      </Flex>
    </section>

    <section>
      {(values.properties ?? 0) === 0 && <Flex align="center" gap={10} style={{ marginBottom: 12 }}>
        <Switch checked={values.createNewPropertyAutomatically} onChange={createNewPropertyAutomatically => update({ createNewPropertyAutomatically })} />
        <Text>Create a new property with this address</Text>
      </Flex>}

      <Text strong><FontAwesomeIcon icon={faMapMarkerAlt} /> Client Billing Address</Text>
      <Flex wrap="wrap" gap={16} style={{ marginTop: 12 }}>
        <FormField label="Address" style={{ width: 300 }}>
          {googleMapsApiKey
            ? <GooglePlacesAutoCompleteInput
                apiKey={googleMapsApiKey}
                maxLength={45}
                placeholder="Street and number"
                value={values.address1 ?? ''}
                onChange={e => update({ address1: e.target.value })}
                onPlaceData={handlePlaceData}
              />
            : <Input maxLength={45} placeholder="Street and number" value={values.address1 ?? ''} onChange={e => update({ address1: e.target.value })} />}
        </FormField>
        <FormField label="Apt / Suite / Unit" style={{ width: 300 }}>
          <Input maxLength={45} value={values.address2 ?? ''} onChange={e => update({ address2: e.target.value })} />
        </FormField>
      </Flex>
      <Flex wrap="wrap" gap={16} style={{ marginTop: 12 }}>
        <FormField label="City" style={{ width: 200 }}>
          <Input maxLength={35} value={values.city ?? ''} onChange={e => update({ city: e.target.value })} />
        </FormField>
        <FormField label="Postal Code / ZIP" style={{ width: 150 }}>
          <Input maxLength={10} value={values.zip ?? ''} onChange={e => update({ zip: e.target.value })} />
        </FormField>
        <FormField label="County / Municipality" style={{ width: 200 }}>
          <Input maxLength={35} value={values.county ?? ''} onChange={e => update({ county: e.target.value })} />
        </FormField>
      </Flex>
      <Flex wrap="wrap" gap={16} style={{ marginTop: 12 }}>
        <FormField label="Country" style={{ width: 200 }}>
          <Select
            allowClear
            value={values.countryCode || undefined}
            options={countryOptions}
            onChange={countryCode => update({ countryCode: countryCode ?? '', state: '' })}
            style={{ width: '100%' }}
          />
        </FormField>
        <FormField label={stateLabel} style={{ width: 200 }}>
          {stateOptions.length > 0
            ? <Select allowClear value={values.state || undefined} options={stateOptions} onChange={state => update({ state: state ?? '' })} style={{ width: '100%' }} />
            : <Input maxLength={35} value={values.state ?? ''} onChange={e => update({ state: e.target.value })} />}
        </FormField>
      </Flex>
    </section>

    <section>
      <Text strong><FontAwesomeIcon icon={faPhone} /> Phone Numbers</Text>
      <VSpace style={{ marginTop: 12 }}>
        {values.phones.map((phone, index) => <Flex key={index} gap={8} align="center" wrap="wrap" style={{ opacity: phone.removed ? 0.45 : 1 }}>
          <Select value={phone.type} options={phoneTypeOptions} onChange={type => updatePhone(index, { type })} style={{ width: 120 }} disabled={phone.removed} />
          <Input placeholder="Phone number" maxLength={20} value={phone.number ?? ''} onChange={e => updatePhone(index, { number: e.target.value })} style={{ width: 180 }} disabled={phone.removed} />
          <Flex align="center" gap={6}>
            <Switch size="small" checked={phone.isPrimary} onChange={isPrimary => updatePhone(index, { isPrimary })} disabled={phone.removed} />
            <Text type="secondary"><small>Primary</small></Text>
          </Flex>
          <Input placeholder="Notes" value={phone.description ?? ''} onChange={e => updatePhone(index, { description: e.target.value })} style={{ width: 200 }} disabled={phone.removed} />
          <Button type="text" icon={<FontAwesomeIcon icon={phone.removed ? faTrashUndo : faTrash} />} onClick={() => removePhone(index)} />
        </Flex>)}
        <div>
          <Button type="primary" icon={<FontAwesomeIcon icon={faPhone} />} onClick={() => update({ phones: [...values.phones, { ...createNewPhone(values.id), number: '', isPrimary: false }] })}>
            Add phone contact
          </Button>
        </div>
      </VSpace>
    </section>

    <section>
      <Text strong><FontAwesomeIcon icon={faEnvelope} /> Emails</Text>
      <VSpace style={{ marginTop: 12 }}>
        {values.emails.map((email, index) => <Flex key={index} gap={8} align="center" wrap="wrap" style={{ opacity: email.removed ? 0.45 : 1 }}>
          <Input type="email" placeholder="Email" maxLength={45} value={email.address ?? ''} onChange={e => updateEmail(index, { address: e.target.value })} style={{ width: 240 }} disabled={email.removed} />
          <Flex align="center" gap={6}>
            <Switch size="small" checked={email.isPrimary} onChange={isPrimary => updateEmail(index, { isPrimary })} disabled={email.removed} />
            <Text type="secondary"><small>Primary</small></Text>
          </Flex>
          <Input placeholder="Notes" value={email.description ?? ''} onChange={e => updateEmail(index, { description: e.target.value })} style={{ width: 200 }} disabled={email.removed} />
          <Button type="text" icon={<FontAwesomeIcon icon={email.removed ? faTrashUndo : faTrash} />} onClick={() => removeEmail(index)} />
        </Flex>)}
        <div>
          <Button type="primary" icon={<FontAwesomeIcon icon={faEnvelope} />} onClick={() => update({ emails: [...values.emails, { ...createNewEmail(values.id), address: '', isPrimary: false }] })}>
            Add email address
          </Button>
        </div>
      </VSpace>
    </section>

    <section>
      <Flex justify="space-between" align="center">
        <Text strong><FontAwesomeIcon icon={faBullseyePointer} /> Sales Leads</Text>
        {onAddSalesLead && !disabled && <Button icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => onAddSalesLead(selectSalesLead)}>
          Add Sales Lead
        </Button>}
      </Flex>
      <Flex wrap="wrap" gap={16} style={{ marginTop: 12 }}>
        {salesLeads.map(lead => <Flex key={lead} align="center" gap={6}>
          <Switch
            size="small"
            checked={values.selectedSalesLeads[lead] === true}
            onChange={checked => update({ selectedSalesLeads: { ...values.selectedSalesLeads, [lead]: checked } })}
          />
          <Text>{lead}</Text>
        </Flex>)}
      </Flex>
      <div style={{ marginTop: 16 }}>
        <FormField label={<span><FontAwesomeIcon icon={faStickyNote} /> Lead Notes</span>} style={{ width: '100%' }}>
          <Input.TextArea rows={5} value={values.salesLeadNotes ?? ''} onChange={e => update({ salesLeadNotes: e.target.value })} />
        </FormField>
      </div>
    </section>

    {consultsSlot}

    <section>
      <FormField label={<span><FontAwesomeIcon icon={faTags} /> Tags</span>} style={{ width: '100%' }}>
        <Select
          mode="tags"
          allowClear
          placeholder="add customer tags"
          value={values.tagsList}
          options={(tagOptions ?? []).map(t => ({ label: t, value: t }))}
          onSearch={onTagSearch}
          onChange={tagsList => update({ tagsList })}
          style={{ width: '100%' }}
        />
      </FormField>
      <div style={{ marginTop: 16 }}>
        <FormField label={<span><FontAwesomeIcon icon={faStickyNote} /> Notes</span>} style={{ width: '100%' }}>
          <Input.TextArea rows={5} value={values.notes ?? ''} onChange={e => update({ notes: e.target.value })} />
        </FormField>
      </div>
    </section>

    <SinceDateOverrideModal
      override={sinceOverride}
      onCancel={() => setSinceOverride(null)}
      onSetValue={(fieldName: SinceDateField, value: string | null) => {
        update({ [fieldName]: value } as Partial<CustomerFormValues>)
        setSinceOverride(null)
      }}
    />
  </div>
}

type FormFieldProps = {
  label: React.ReactNode
  style?: React.CSSProperties
  children: React.ReactNode
}

const FormField = ({ label, style, children }: FormFieldProps) => (
  <Space direction="vertical" size={2} style={style}>
    <Text type="secondary"><small>{label}</small></Text>
    {children}
  </Space>
)

export default CustomerInfoForm
